import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { condutorService, reboqueService } from "../services/transporteService";
import type { SalvarCondutorRequest, SalvarReboqueRequest } from "../types";

// ── Condutor ──────────────────────────────────────────────────────────────

export function useCondutores(params?: { busca?: string; ativo?: boolean }) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["condutores", params],
    queryFn: () => condutorService.listar(params),
  });

  const condutores = data?.itens ?? [];

  const criar = useMutation({
    mutationFn: (req: SalvarCondutorRequest) => condutorService.criar(req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["condutores"] });
      toast.success("Condutor cadastrado com sucesso!");
    },
    onError: (err: Error) => toast.error("Erro ao cadastrar condutor: " + err.message),
  });

  const atualizar = useMutation({
    mutationFn: ({ id, req }: { id: number; req: SalvarCondutorRequest }) =>
      condutorService.atualizar(id, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["condutores"] });
      toast.success("Condutor atualizado com sucesso!");
    },
    onError: (err: Error) => toast.error("Erro ao atualizar condutor: " + err.message),
  });

  const excluir = useMutation({
    mutationFn: (id: number) => condutorService.excluir(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["condutores"] });
      toast.success("Condutor inativado com sucesso!");
    },
    onError: (err: Error) => toast.error("Erro ao inativar condutor: " + err.message),
  });

  return { condutores, isLoading, total: data?.total ?? 0, criar, atualizar, excluir };
}

export function useCondutoresCombo() {
  return useQuery({
    queryKey: ["condutores-combo"],
    queryFn: () => condutorService.combo(),
  });
}

// ── Reboque ───────────────────────────────────────────────────────────────

export function useReboques(params?: { busca?: string; ativo?: boolean }) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["reboques", params],
    queryFn: () => reboqueService.listar(params),
  });

  const reboques = data?.itens ?? [];

  const criar = useMutation({
    mutationFn: (req: SalvarReboqueRequest) => reboqueService.criar(req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reboques"] });
      toast.success("Reboque cadastrado com sucesso!");
    },
    onError: (err: Error) => toast.error("Erro ao cadastrar reboque: " + err.message),
  });

  const atualizar = useMutation({
    mutationFn: ({ id, req }: { id: number; req: SalvarReboqueRequest }) =>
      reboqueService.atualizar(id, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reboques"] });
      toast.success("Reboque atualizado com sucesso!");
    },
    onError: (err: Error) => toast.error("Erro ao atualizar reboque: " + err.message),
  });

  const excluir = useMutation({
    mutationFn: (id: number) => reboqueService.excluir(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reboques"] });
      toast.success("Reboque inativado com sucesso!");
    },
    onError: (err: Error) => toast.error("Erro ao inativar reboque: " + err.message),
  });

  return { reboques, isLoading, total: data?.total ?? 0, criar, atualizar, excluir };
}

export function useReboquesCombo() {
  return useQuery({
    queryKey: ["reboques-combo"],
    queryFn: () => reboqueService.combo(),
  });
}
